import { useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { UserPlus, AlertCircle } from 'lucide-react'
import { useAuth } from '../context/AuthContext'
import { ApiError } from '../lib/api'
import { DJ_NAME } from '../data'

export default function SignUp() {
  const { signUp } = useAuth()
  const navigate = useNavigate()

  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError('')
    if (password.length < 8) { setError('Password must be at least 8 characters'); return }
    setLoading(true)
    try {
      await signUp(name, email, password)
      navigate('/', { replace: true })
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Sign up failed')
    } finally {
      setLoading(false)
    }
  }

  const inputStyle: React.CSSProperties = {
    width: '100%', background: 'var(--ink)', border: '1px solid var(--border)',
    color: 'var(--cream)', fontFamily: 'var(--font-body)', fontSize: '14px',
    padding: '12px 14px', outline: 'none', transition: 'border-color 0.2s', borderRadius: 0,
  }
  const labelStyle: React.CSSProperties = {
    fontFamily: 'var(--font-display)', fontSize: '10px', letterSpacing: '3px',
    textTransform: 'uppercase', color: 'var(--gold-dim)', display: 'block', marginBottom: '8px',
  }

  return (
    <main style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '80px 20px', background: 'var(--ink)' }}>
      <div style={{ width: '100%', maxWidth: '400px' }}>
        <Link to="/" style={{ display: 'block', fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: '20px', letterSpacing: '5px', color: 'var(--gold)', textTransform: 'uppercase', textDecoration: 'none', textAlign: 'center', marginBottom: '40px' }}>
          {DJ_NAME}
        </Link>
        <div style={{ background: 'var(--ink-2)', border: '1px solid var(--border)', padding: '40px 32px' }}>
          <h1 style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: '24px', letterSpacing: '3px', textTransform: 'uppercase', color: 'var(--cream)', marginBottom: '8px', textAlign: 'center' }}>
            Join the Fan Club
          </h1>
          <p style={{ textAlign: 'center', fontSize: '13px', color: 'var(--muted)', marginBottom: '28px' }}>
            Early access to mixes, exclusive content, and presales.
          </p>
          {error && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'rgba(200,50,50,0.12)', border: '1px solid rgba(200,50,50,0.3)', color: '#e08080', padding: '10px 14px', marginBottom: '20px', fontSize: '13px' }}>
              <AlertCircle size={14} />{error}
            </div>
          )}
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {[
              { label: 'Full Name', type: 'text', value: name, setter: setName, placeholder: 'Your name', minLength: 2 },
              { label: 'Email Address', type: 'email', value: email, setter: setEmail, placeholder: 'you@example.com' },
              { label: 'Password', type: 'password', value: password, setter: setPassword, placeholder: '8+ characters', minLength: 8 },
            ].map(({ label, type, value, setter, placeholder, minLength }) => (
              <div key={label}>
                <label style={labelStyle}>{label}</label>
                <input type={type} required value={value} minLength={minLength}
                  onChange={(e) => setter(e.target.value)} placeholder={placeholder} style={inputStyle}
                  onFocus={(e) => (e.target.style.borderColor = 'var(--gold-dim)')}
                  onBlur={(e) => (e.target.style.borderColor = 'var(--border)')} />
              </div>
            ))}
            <button type="submit" disabled={loading} className="btn-primary"
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', width: '100%', opacity: loading ? 0.7 : 1, cursor: loading ? 'wait' : 'pointer' }}>
              <UserPlus size={14} />{loading ? 'Creating Account...' : 'Create Account'}
            </button>
          </form>
          <p style={{ textAlign: 'center', marginTop: '24px', fontSize: '13px', color: 'var(--muted)' }}>
            Already have an account?{' '}
            <Link to="/signin" style={{ color: 'var(--gold)', textDecoration: 'none' }}>Sign In</Link>
          </p>
        </div>
      </div>
    </main>
  )
}
